import ClickAwayListener from "@mui/material/ClickAwayListener";
import { useRef, useState } from "react";
import { useGetRelation } from "src/hooks/useRelations";
import { QuestionsListSettingsProvider } from "src/reducers/questions";

import { getSelectedLinkDetails } from "../../Relations/plugins";
import QuestionView from "../Search/QuestionsView/QuestionView";
import ArticlesView from "./ArticlesView";
import Container from "./Container";
import Controllers from "./Controllers";

interface Answer {
	questionId: string | number;
	answer: unknown;
}

interface RelationViewProps {
	anchorEl: HTMLElement | null;
	setAnchorEl: (el: HTMLElement | null) => void;
}

function RelationView({ anchorEl, setAnchorEl }: RelationViewProps) {
	const ref = useRef<{ timeout?: ReturnType<typeof setTimeout> }>({});
	const [answers, setAnswers] = useState<Answer[]>([]);
	const [index, setIndex] = useState(0);
	const { relationHash } = getSelectedLinkDetails(anchorEl);
	const { isLoading, data } = useGetRelation(relationHash);

	if (isLoading) {
		return null;
	}

	const articles = data.link.linksItems.filter(
		(item: { itemType: string }) => item.itemType === "article",
	);
	const open = Boolean(anchorEl);
	const id = open ? "relation" : undefined;

	const onMouseLeave = () => {
		clearTimeout(ref.current.timeout);
		ref.current.timeout = setTimeout(() => {
			setAnchorEl(null);
		}, 200);
	};

	const renderArticles = () => {
		if (articles.length === 0) {
			return null;
		}

		return <ArticlesView articles={articles} />;
	};

	const getQuestionsWithAnswers = () => {
		if (!data.questions) {
			return null;
		}

		const answersObject = answers.reduce<Record<string | number, unknown>>((acc, { questionId, answer }) => {
			acc[questionId] = answer;

			return acc;
		}, {});

		return data.questions.map((question: { id: string | number }) => {
			return {
				...question,
				answer: answersObject[question.id] || null,
			};
		});
	};

	const renderQuestions = () => {
		if (!data.questions || data.questions.length === 0) {
			return null;
		}
		const { length } = data.questions;

		return (
			<div className="flex flex-col px-4">
				{length > 1 && (
					<Controllers
						onChange={setIndex}
						currentIndex={index}
						length={length}
					/>
				)}
				<QuestionView
					className="question-relation-view"
					question={getQuestionsWithAnswers()?.[index]}
					setAnswer={(answer: Answer) => setAnswers([...answers, answer])}
					searchValue=""
					disabledSettings
				/>
			</div>
		);
	};

	return (
		<ClickAwayListener onClickAway={() => setAnchorEl(null)}>
			<Container
				anchorEl={anchorEl}
				setAnchorEl={setAnchorEl}
				id={id}
				open={open}
				onMouseLeave={onMouseLeave}
			>
				<QuestionsListSettingsProvider>
					<div className="relation-view basic-box">
						{renderQuestions()}
						{renderArticles()}
					</div>
				</QuestionsListSettingsProvider>
			</Container>
		</ClickAwayListener>
	);
}

export default RelationView;
